import { Controller } from "@hotwired/stimulus"

export default class extends Controller {

    static targets = [
        "dayWrapper",
        "statusWrapper",
        "colorWrapper",
        "nameInput",
        "existingList",
    ]

    static values = {
        existingUrl: String,
        currentId: Number,
    }

    declare readonly dayWrapperTarget: HTMLElement
    declare readonly hasDayWrapperTarget: boolean

    declare readonly statusWrapperTarget: HTMLElement
    declare readonly hasStatusWrapperTarget: boolean

    declare readonly colorWrapperTarget: HTMLElement
    declare readonly hasColorWrapperTarget: boolean

    declare readonly nameInputTarget: HTMLInputElement
    declare readonly hasNameInputTarget: boolean

    declare readonly existingListTarget: HTMLElement
    declare readonly hasExistingListTarget: boolean

    declare readonly existingUrlValue: string
    declare readonly hasExistingUrlValue: boolean
    declare readonly currentIdValue: number

    private searchTimer?: number
    private abortController?: AbortController

    connect(): void {

        this.refresh()
        this.searchExisting()

        const typeSelect =
            document.querySelector(
                '#tag_type'
            ) as HTMLSelectElement

        if (!typeSelect) {
            return
        }

        typeSelect.addEventListener(
            'change',
            () => this.refresh()
        )
    }

    disconnect(): void {
        if (this.searchTimer) {
            window.clearTimeout(this.searchTimer)
        }

        this.abortController?.abort()
    }

    searchExisting(): void {
        if (this.searchTimer) {
            window.clearTimeout(this.searchTimer)
        }

        this.searchTimer = window.setTimeout(() => {
            void this.loadExistingTags()
        }, 250)
    }

    refresh(): void {

        const typeSelect =
            document.querySelector(
                '#tag_type'
            ) as HTMLSelectElement

        if (!typeSelect) {
            return
        }

        const text =
            typeSelect.selectedOptions[0]
                ?.textContent
                ?.toLowerCase() || ''

        const isSchedule =
            text.includes('horario')

        // =========================
        // SHOW/HIDE
        // =========================

        if (this.hasDayWrapperTarget) {
            this.dayWrapperTarget.classList.toggle('d-none', !isSchedule)
        }

        if (this.hasStatusWrapperTarget) {
            this.statusWrapperTarget.classList.toggle('d-none', !isSchedule)
        }

        // Hide color field when a type is selected (type provides its own color)
        const hasType = typeSelect.value !== ''
        if (this.hasColorWrapperTarget) {
            this.colorWrapperTarget.classList.toggle('d-none', hasType)
        }

        // =========================
        // READONLY SCHEDULE
        // =========================

        const nameInput =
            document.querySelector(
                '#tag_name'
            ) as HTMLInputElement

        if (nameInput) {

            nameInput.readOnly =
                isSchedule
        }

        // =========================
        // AUTO NAME
        // =========================

        if (isSchedule) {

            const day =
                (
                    document.querySelector(
                        '#tag_day'
                    ) as HTMLSelectElement
                )?.value || ''

            const status =
                (
                    document.querySelector(
                        '#tag_scheduleStatus'
                    ) as HTMLSelectElement
                )?.selectedOptions[0]
                    ?.textContent || ''

            if (day && status) {

                nameInput.value = status
                this.searchExisting()
            }
        }
    }

    private async loadExistingTags(): Promise<void> {
        if (!this.hasNameInputTarget || !this.hasExistingListTarget || !this.hasExistingUrlValue) {
            return
        }

        const query = this.nameInputTarget.value.trim()

        if (query.length < 2) {
            this.hideExistingList()
            return
        }

        this.abortController?.abort()
        this.abortController = new AbortController()

        const url = new URL(this.existingUrlValue, window.location.origin)
        url.searchParams.set('q', query)

        if (this.currentIdValue > 0) {
            url.searchParams.set('exclude', String(this.currentIdValue))
        }

        try {
            const response = await fetch(url.toString(), {
                headers: { Accept: 'application/json' },
                signal: this.abortController.signal,
            })

            if (!response.ok) {
                this.hideExistingList()
                return
            }

            const data = await response.json()
            this.renderExistingTags(Array.isArray(data.tags) ? data.tags : [])
        } catch (error) {
            if ((error as DOMException).name !== 'AbortError') {
                this.hideExistingList()
            }
        }
    }

    private renderExistingTags(tags: Array<{ id: number; name: string; registers: number }>): void {
        if (tags.length === 0) {
            this.hideExistingList()
            return
        }

        const items = tags
            .map((tag) => `
                <li class="list-group-item d-flex justify-content-between align-items-center py-1 px-2">
                    <span>${this.escapeHtml(tag.name)}</span>
                    <span class="badge text-bg-light">${tag.registers}</span>
                </li>
            `)
            .join('')

        this.existingListTarget.innerHTML = `
            <div class="small text-muted mb-1">Tags libres existentes</div>
            <ul class="list-group list-group-flush border rounded">${items}</ul>
        `
        this.existingListTarget.classList.remove('d-none')
    }

    private hideExistingList(): void {
        if (!this.hasExistingListTarget) {
            return
        }

        this.existingListTarget.innerHTML = ''
        this.existingListTarget.classList.add('d-none')
    }

    private escapeHtml(value: string): string {
        const div = document.createElement('div')
        div.textContent = value
        return div.innerHTML
    }
}
